import { useMutation, useQueryClient } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import {
  getFeedIdentityGeneration,
  replaceConfirmedFeedPost,
  scheduleFeedReconciliation,
} from "@bw/shared/hooks";
import type { PostResponse, PostUpdate } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/** The variables one edit carries: which post + ONLY the changed fields (the
 * PATCH body has exclude_unset semantics — an omitted field is left untouched
 * server-side, so callers must NOT restate unchanged fields). */
export interface UpdatePostVars {
  postId: string;
  data: PostUpdate;
}

/**
 * Edit a post (author-only PATCH /communities/posts/{post_id}). Mirrors
 * useLikePost: NON-OPTIMISTIC. The edited content / media / "· Edited" marker are
 * NEVER written before confirmation. On success the exact server response replaces
 * every loaded Feed card, post-detail queries invalidate, and bounded projection
 * reconciliation follows. The server-set `edited_at` remains authoritative.
 *
 * The server enforces author-only (403), 404 on a missing post, and 422 when the
 * merged result would be invalid (a non-repost must keep non-empty content OR
 * >= 1 media; a repost edits only its quote and can never gain media; media cap
 * 4). A rejected edit leaves every surface unchanged and lands on
 * `mutation.error` for the dialog to surface honestly via role="alert" with the
 * draft intact.
 *
 * Re-entrancy is the dialog's concern: it disables Save while `isPending` so a
 * double-click fires the mutation once.
 */
export function useUpdatePost() {
  const queryClient = useQueryClient();

  return useMutation<PostResponse, unknown, UpdatePostVars, number>({
    onMutate: () => getFeedIdentityGeneration(queryClient),
    mutationFn: ({ postId, data }: UpdatePostVars) =>
      communityApi.updatePost(postId, data),
    onSuccess: (post, _variables, generation) => {
      if (generation !== getFeedIdentityGeneration(queryClient)) return;
      // Refresh post-detail consumers; canonical Feed cards are replaced below
      // with the exact confirmed response and then reconciled.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.social.all,
      });
      replaceConfirmedFeedPost(queryClient, post);
      scheduleFeedReconciliation(queryClient, [
        { scope: "home" },
        { scope: "friends" },
        { scope: "communities" },
        { scope: "discovery" },
        { scope: "user", subject: post.author_id },
        ...(post.community_id
          ? [{ scope: "community" as const, subject: post.community_id }]
          : []),
      ]);
    },
  });
}
